import React, { useState } from "react";
import { useTranslation } from "react-i18next"; // <-- Importamos para traducciones
import {  AlertCircle,Check, X } from "lucide-react";
import "./SubscriptionView.css";

const SubscriptionView = () => {
  const [showContactDialog, setShowContactDialog] = useState(false);
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [showNotification, setShowNotification] = useState(false);
  const [notificationType, setNotificationType] = useState("");

  // Hook de traducción
  const { t } = useTranslation();

  const handleContactClick = () => {
    setShowContactDialog(true);
  };

  const handleSendContact = async () => {
    if (!email.trim() || !message.trim()) return;

    try {
      setLoading(true);
      // Simulamos un envío (2 segundos)
      await new Promise((resolve) => setTimeout(resolve, 2000));

      setNotificationType("success");
      setShowNotification(true);

      setTimeout(() => {
        setShowNotification(false);
        setShowContactDialog(false);
        setMessage("");
      }, 2000);
    } catch (error) {
      setNotificationType("error");
      setShowNotification(true);
      setTimeout(() => {
        setShowNotification(false);
      }, 2000);
    } finally {
      setLoading(false);
    }
  };

  // Datos de los planes (sin Starter, PRO ajustado)
  const plans = [
    {
      id: "2f319c07-35df-4c05-b6da-b7334fdc59dd",
      tier_type: "FREE",
      display_name: "Free Plan",
      description: "Perfect for getting started with basic automation and AI assistance",
      monthly_price_usd: 0,
      yearly_price_usd: 0,
      max_workflow_executions_per_month: 30,
      max_assistant_requests_per_month: 60,
      max_database_calls_per_month: 200,
      is_active: true,
      sort_order: 1,
    },
    {
      id: "12129832-37f8-4f1e-bad3-19fc44c3ff42",
      tier_type: "DEVELOPER",
      display_name: "Developer Plan",
      description: "Ideal for small teams and growing businesses with moderate automation needs",
      monthly_price_usd: 25,
      yearly_price_usd: 249,
      max_workflow_executions_per_month: 100,
      max_assistant_requests_per_month: 200,
      max_database_calls_per_month: 30000,
      is_active: true,
      sort_order: 2,
    },
    {
      id: "12129832-37f8-4f1e-bad3-19fc44c3ff41",
      tier_type: "PRO",
      display_name: "Professional Plan",
      description: "Ideal for small teams and growing businesses with moderate automation needs",
      monthly_price_usd: 199,
      yearly_price_usd: 1990,
      max_workflow_executions_per_month: 5000,
      max_assistant_requests_per_month: 10000,
      max_database_calls_per_month: 30000,
      is_active: true,
      sort_order: 3,
    },
    {
      id: "3af509e1-548b-4e30-85b5-56b3d60abeaa",
      tier_type: "ENTERPRISE",
      display_name: "Enterprise Plan",
      description: "Unlimited power for large organizations with custom requirements and premium support",
      monthly_price_usd: null,
      yearly_price_usd: null,
      max_workflow_executions_per_month: null,
      max_assistant_requests_per_month: null,
      max_database_calls_per_month: null,
      is_active: true,
      sort_order: 4,
    },
  ];

  return (
    <div className="min-h-screen bg-black text-white p-8">
      <div className="max-w-7xl mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-white mb-2">Plans & Pricing</h1>
          <p className="text-gray-400">Choose the best plan for your team and automation needs.</p>
        </div>

        <div className="mb-8">
          <h2 className="text-xl font-semibold text-white mb-4">Current Subscription</h2>
          <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl p-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-white">Free Plan</h3>
                <p className="text-sm text-gray-400">Perfect for getting started</p>
              </div>
              <div className="text-right">
                <div className="text-2xl font-bold text-white">$0</div>
                <div className="text-sm text-gray-400">per month</div>
              </div>
            </div>
          </div>
        </div>

        <div>
          <h2 className="text-xl font-semibold text-white mb-6">Available Plans</h2>
          <div className="flex gap-2 justify-center">
            {plans.map((plan) => (
              <div 
                key={plan.id}
                className={`bg-white/10 backdrop-blur-sm border rounded-2xl p-3 transition-all duration-200 hover:bg-white/15 hover:border-white/30 flex flex-col w-[220px] h-[380px] ${
                  plan.tier_type === 'FREE' 
                    ? 'border-purple-400/40 bg-white/15' 
                    : 'border-white/20'
                }`}
              >
                <div className="flex-1 flex flex-col">
                  <h3 className="text-base font-semibold text-white mb-2">{plan.display_name}</h3>
                  <p className="text-xs text-gray-400 mb-3 flex-grow leading-tight">{plan.description}</p>
                  
                  <div className="mb-3">
                    {plan.tier_type === 'ENTERPRISE' ? (
                      <div>
                        <div className="text-xl font-bold text-white">Custom</div>
                        <div className="text-xs text-gray-400">Contact for pricing</div>
                      </div>
                    ) : (
                      <div>
                        <div className="text-xl font-bold text-white">${plan.monthly_price_usd}</div>
                        <div className="text-xs text-gray-400">per month</div>
                        {plan.yearly_price_usd > 0 && (
                          <div className="text-xs text-gray-400">${plan.yearly_price_usd}/year</div>
                        )}
                      </div>
                    )}
                  </div>

                  <div className="space-y-1 text-xs text-gray-400 mb-4 flex-grow">
                    <div className="flex justify-between">
                      <span>Workflows:</span>
                      <span className="text-white font-medium">
                        {plan.tier_type === 'ENTERPRISE' ? 'Custom' : plan.max_workflow_executions_per_month.toLocaleString()}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span>Assistants:</span>
                      <span className="text-white font-medium">
                        {plan.tier_type === 'ENTERPRISE' ? 'Custom' : plan.max_assistant_requests_per_month.toLocaleString()}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="mt-auto">
                  {plan.tier_type === 'ENTERPRISE' ? (
                    <button 
                      onClick={handleContactClick} 
                      className="w-full bg-purple-600 hover:bg-purple-700 text-white px-3 py-2 rounded-lg text-sm font-medium transition-colors duration-200"
                    >
                      Contact Us
                    </button>
                  ) : plan.tier_type === 'FREE' ? (
                    <button className="w-full bg-gray-600 text-gray-300 px-3 py-2 rounded-lg text-sm font-medium cursor-not-allowed">
                      Current Plan
                    </button>
                  ) : (
                    <button className="w-full bg-white/20 hover:bg-white/30 text-white px-3 py-2 rounded-lg text-sm font-medium transition-colors duration-200 border border-white/40">
                      Select Plan
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Contact Dialog */}
      {showContactDialog && (
        <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-[#18181b] border border-white/20 rounded-2xl p-6 w-full max-w-md">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold text-white">{t("subscriptionView.dialog.title")}</h2>
              <button
                className="text-gray-400 hover:text-white p-1"
                onClick={() => {
                  setShowContactDialog(false);
                  setMessage("");
                }}
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-white mb-1 text-sm">{t("subscriptionView.dialog.fromLabel")}</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t("subscriptionView.dialog.placeholderEmail")}
                  className="w-full bg-black/60 border border-white/40 text-white placeholder-gray-500 rounded-lg px-4 py-2"
                />
              </div>

              <div>
                <textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder={t("subscriptionView.dialog.placeholderMessage")}
                  className="w-full bg-black/60 border border-white/40 text-white placeholder-gray-500 rounded-lg px-4 py-2 h-24 resize-none"
                />
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                className="flex-1 bg-gray-700 hover:bg-gray-800 text-white px-4 py-2 rounded-lg transition-colors duration-200"
                onClick={() => {
                  setShowContactDialog(false);
                  setMessage("");
                }}
                disabled={loading}
              >
                {t("subscriptionView.dialog.cancel")}
              </button>
              <button
                className="flex-1 bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg transition-colors duration-200 disabled:bg-gray-600 disabled:cursor-not-allowed"
                onClick={handleSendContact}
                disabled={loading || !message.trim() || !email.trim()}
              >
                {loading
                  ? t("subscriptionView.dialog.sending")
                  : t("subscriptionView.dialog.send")}
              </button>
            </div>

            {/* Notificación */}
            {showNotification && (
              <div className={`mt-4 p-4 rounded-lg flex items-center gap-3 ${
                notificationType === "success" 
                  ? "bg-green-900/30 text-green-400 border border-green-500/30" 
                  : "bg-red-900/30 text-red-400 border border-red-500/30"
              }`}>
                {notificationType === "success" ? (
                  <Check size={20} />
                ) : (
                  <AlertCircle size={20} />
                )}
                <span className="flex-1">
                  {notificationType === "success"
                    ? t("subscriptionView.dialog.sentSuccessfully")
                    : t("subscriptionView.dialog.somethingWentWrong")}
                </span>
                <button onClick={() => setShowNotification(false)}>
                  <X size={16} />
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SubscriptionView;
